import React, { useEffect, useState, useRef } from 'react';
import { useTranslations } from 'use-intl';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import {
	useSignContractOpenfinVanCreditMutation,
	useDownloadContractOpenfinVanCreditMutation
} from '@/graphql/__generated__/van-credits';
import { Upload, X } from 'react-feather';
import Button from '@/UIlibrary/Button/Button';
import { Typography } from '@/UIlibrary/Typography/Typography';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { toast } from 'react-hot-toast';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';

interface SignedDocumentsProps {
	clientId: string;
	creditId: string;
	refetchTrigger?: number;
}

interface ValidationErrors {
	contract?: string;
	promissoryNote?: string;
}

interface FilePreview {
	file: File;
	url: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const SignedDocuments: React.FC<SignedDocumentsProps> = ({ creditId, refetchTrigger }) => {
	const t = useTranslations('SignedDocuments');
	const vanCreditsClient = useApolloClient(SCHEMAS.VAN_CREDITS);

	const [contractPreview, setContractPreview] = useState<FilePreview | null>(null);
	const [promissoryNotePreview, setPromissoryNotePreview] = useState<FilePreview | null>(null);
	const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [signedDocsData, setSignedDocsData] = useState<{
		creditContract: string | null;
		promissoryNote: string | null;
	}>({ creditContract: null, promissoryNote: null });
	const [downloadLoading, setDownloadLoading] = useState(true);
	const [downloadError, setDownloadError] = useState<string | null>(null);
	const [showReuploadForm, setShowReuploadForm] = useState(false);

	const contractInputRef = useRef<HTMLInputElement>(null);
	const promissoryNoteInputRef = useRef<HTMLInputElement>(null);

	const [signContractMutation] = useSignContractOpenfinVanCreditMutation({
		client: vanCreditsClient
	});

	const [downloadContractMutation] = useDownloadContractOpenfinVanCreditMutation({
		client: vanCreditsClient
	});

	useEffect(() => {
		fetchSignedDocuments();
	}, [creditId, refetchTrigger]);

	useEffect(() => {
		return () => {
			if (contractPreview) {
				URL.revokeObjectURL(contractPreview.url);
			}
			if (promissoryNotePreview) {
				URL.revokeObjectURL(promissoryNotePreview.url);
			}
		};
	}, [contractPreview, promissoryNotePreview]);

	const fetchSignedDocuments = async () => {
		setDownloadLoading(true);
		setDownloadError(null);

		try {
			const { data } = await downloadContractMutation({
				variables: {
					input: {
						creditId
					}
				}
			});

			if (data?.downloadContractOpenfinVanCredit?.successful) {
				const result = data.downloadContractOpenfinVanCredit.result;
				if (result?.creditContract || result?.promissoryNote) {
					setSignedDocsData({
						creditContract: result.creditContract || null,
						promissoryNote: result.promissoryNote || null
					});
				}
			} else if (data?.downloadContractOpenfinVanCredit?.mutationErrors?.length) {
				const errorMessage = data.downloadContractOpenfinVanCredit.mutationErrors[0]?.message;
				setDownloadError(errorMessage || t('ErrorLoadingDocuments'));
			}
		} catch {
			setDownloadError(t('ErrorLoadingDocuments'));
		} finally {
			setDownloadLoading(false);
		}
	};

	const validateFile = (file: File): string | null => {
		if (file.type !== 'application/pdf') {
			return t('InvalidFileType');
		}
		if (file.size > MAX_FILE_SIZE) {
			return t('FileTooLarge');
		}
		return null;
	};

	const handleFileSelect = (
		event: React.ChangeEvent<HTMLInputElement>,
		type: 'contract' | 'promissoryNote'
	) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const error = validateFile(file);

		if (error) {
			setValidationErrors((prev) => ({ ...prev, [type]: error }));
			if (type === 'contract') {
				setContractPreview(null);
			} else {
				setPromissoryNotePreview(null);
			}
			return;
		}

		setValidationErrors((prev) => ({ ...prev, [type]: undefined }));

		const url = URL.createObjectURL(file);
		const preview = { file, url };

		if (type === 'contract') {
			if (contractPreview) {
				URL.revokeObjectURL(contractPreview.url);
			}
			setContractPreview(preview);
		} else {
			if (promissoryNotePreview) {
				URL.revokeObjectURL(promissoryNotePreview.url);
			}
			setPromissoryNotePreview(preview);
		}
	};

	const handleRemoveFile = (type: 'contract' | 'promissoryNote') => {
		if (type === 'contract') {
			if (contractPreview) {
				URL.revokeObjectURL(contractPreview.url);
			}
			setContractPreview(null);
			if (contractInputRef.current) {
				contractInputRef.current.value = '';
			}
		} else {
			if (promissoryNotePreview) {
				URL.revokeObjectURL(promissoryNotePreview.url);
			}
			setPromissoryNotePreview(null);
			if (promissoryNoteInputRef.current) {
				promissoryNoteInputRef.current.value = '';
			}
		}
		setValidationErrors((prev) => ({ ...prev, [type]: undefined }));
	};

	const handleUploadClick = () => {
		if (!contractPreview || !promissoryNotePreview) {
			toast.error(t('BothFilesRequired'));
			return;
		}
		setShowConfirmModal(true);
	};

	const handleConfirmUpload = async () => {
		if (!contractPreview || !promissoryNotePreview) return;

		setUploadLoading(true);
		try {
			const { data } = await signContractMutation({
				variables: {
					input: {
						creditId,
						creditContract: contractPreview.file,
						promissoryNote: promissoryNotePreview.file
					}
				}
			});

			if (data?.signContractOpenfinVanCredit?.successful) {
				toast.success(t('UploadSuccess'));
				setShowConfirmModal(false);
				setShowReuploadForm(false);
				setContractPreview(null);
				setPromissoryNotePreview(null);
				if (contractInputRef.current) {
					contractInputRef.current.value = '';
				}
				if (promissoryNoteInputRef.current) {
					promissoryNoteInputRef.current.value = '';
				}
				await fetchSignedDocuments();
			} else if (data?.signContractOpenfinVanCredit?.mutationErrors?.length) {
				const errorMessage = data.signContractOpenfinVanCredit.mutationErrors[0]?.message;
				toast.error(errorMessage || t('UploadError'));
			}
		} catch {
			toast.error(t('UploadError'));
		} finally {
			setUploadLoading(false);
		}
	};

	const downloadFromS3Url = (url: string, filename: string) => {
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.target = '_blank';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const handleDownloadContract = () => {
		if (signedDocsData.creditContract) {
			downloadFromS3Url(signedDocsData.creditContract, `contrato-firmado-${creditId}.pdf`);
		}
	};

	const handleDownloadPromissoryNote = () => {
		if (signedDocsData.promissoryNote) {
			downloadFromS3Url(signedDocsData.promissoryNote, `pagare-firmado-${creditId}.pdf`);
		}
	};

	const renderFileInput = (
		type: 'contract' | 'promissoryNote',
		label: string,
		preview: FilePreview | null,
		inputRef: React.RefObject<HTMLInputElement>
	) => {
		const error = validationErrors[type];

		return (
			<div className="flex flex-col">
				<div className="mb-2">
					<Typography weight="semibold" size="xl">
						{label}
					</Typography>
				</div>
				<input
					ref={inputRef}
					type="file"
					accept="application/pdf"
					onChange={(e) => handleFileSelect(e, type)}
					className="hidden"
					id={`${type}-input`}
				/>

				<label
					htmlFor={`${type}-input`}
					className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-500 transition-colors"
				>
					<Upload size={20} className="mr-2" />
					<span className="text-sm text-gray-600">
						{type === 'contract' ? t('SelectContract') : t('SelectPromissoryNote')}
					</span>
				</label>

				{error && <p className="text-sm text-red-600 mt-2">{error}</p>}

				{preview && !error && (
					<div className="mt-4">
						<div className="flex items-center justify-between mb-2">
							<Typography size="sm" color="subtitle">
								{preview.file.name}
							</Typography>
							<Button
								onClick={() => handleRemoveFile(type)}
								label={t('Remove')}
								color="error"
								size="small"
								startIcon={<X size={16} />}
							/>
						</div>
						<iframe
							src={preview.url}
							className="w-full h-[50vh] border border-gray-300 rounded"
							title={label}
						/>
					</div>
				)}
			</div>
		);
	};

	const renderSignedDocument = (title: string, url: string | null, onDownload: () => void) => {
		return (
			<div className="flex flex-col">
				<div className="flex items-center justify-between mb-4">
					<Typography weight="semibold" size="lg">
						{title}
					</Typography>
					{url && (
						<Button
							onClick={onDownload}
							label={t('Download')}
							color="primary"
							size="small"
							startIcon={<Upload size={16} />}
						/>
					)}
				</div>

				{url && (
					<iframe
						src={url}
						className="w-full h-[75vh] border border-gray-300 rounded"
						title={title}
					/>
				)}
			</div>
		);
	};

	if (downloadLoading) {
		return (
			<div className="w-full mt-8">
				<Typography weight="semibold" size="xl">
					{t('Title')}
				</Typography>
				<div className="flex items-center justify-center h-[40vh] border border-gray-300 rounded mt-4">
					<div className="flex flex-col items-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
						<p className="mt-4 text-gray-600">{t('LoadingDocuments')}</p>
					</div>
				</div>
			</div>
		);
	}

	const hasSignedDocuments = signedDocsData.creditContract || signedDocsData.promissoryNote;
	const showUploadForm = !hasSignedDocuments || showReuploadForm;

	return (
		<div
			className="flex flex-col p-6 gap-5 rounded-lg mt-5"
			style={{ backgroundColor: GRAY_COLORS.gray50 }}
		>
			<div className="flex items-center justify-between mb-4">
				<Typography weight="semibold" size="lg">
					{t('Title')}
				</Typography>
				{hasSignedDocuments && !showReuploadForm && !downloadError && (
					<Button
						onClick={() => setShowReuploadForm(true)}
						label={t('Reupload')}
						color="primary"
						size="auto"
						startIcon={<Upload size={20} />}
					/>
				)}
			</div>

			{showUploadForm && !downloadError && (
				<div className="mt-4">
					<div className="mb-6">
						<Typography weight="semibold" size="lg">
							{t('UploadTitle')}
						</Typography>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						{renderFileInput('contract', t('Contract'), contractPreview, contractInputRef)}
						{renderFileInput(
							'promissoryNote',
							t('PromissoryNote'),
							promissoryNotePreview,
							promissoryNoteInputRef
						)}
					</div>

					<div className="mt-6 flex justify-end gap-4">
						{hasSignedDocuments && (
							<Button
								onClick={() => {
									setShowReuploadForm(false);
									setContractPreview(null);
									setPromissoryNotePreview(null);
									setValidationErrors({});
									if (contractInputRef.current) {
										contractInputRef.current.value = '';
									}
									if (promissoryNoteInputRef.current) {
										promissoryNoteInputRef.current.value = '';
									}
								}}
								label={t('Cancel')}
								color="gray"
								size="medium"
							/>
						)}
						{contractPreview && promissoryNotePreview && (
							<Button
								onClick={handleUploadClick}
								label={t('Upload')}
								color="primary"
								size="medium"
								startIcon={<Upload size={20} />}
								disabled={uploadLoading}
								loading={uploadLoading}
							/>
						)}
					</div>
				</div>
			)}

			{downloadError && (
				<div className="flex items-center justify-center h-[40vh] border border-red-300 bg-red-50 rounded mt-4">
					<div className="text-center px-4">
						<p className="text-red-600 font-semibold">{t('ErrorLoadingDocuments')}</p>
						<p className="text-sm text-red-500 mt-2">{downloadError}</p>
					</div>
				</div>
			)}

			{hasSignedDocuments && !showReuploadForm && !downloadError && (
				<div className="grid grid-cols-1 gap-6 mt-4">
					{signedDocsData.creditContract &&
						renderSignedDocument(
							t('Contract'),
							signedDocsData.creditContract,
							handleDownloadContract
						)}
					{signedDocsData.promissoryNote &&
						renderSignedDocument(
							t('PromissoryNote'),
							signedDocsData.promissoryNote,
							handleDownloadPromissoryNote
						)}
				</div>
			)}

			<ConfirmModal
				isOpen={showConfirmModal}
				title={t('ConfirmUploadTitle')}
				subtitle={t('ConfirmUploadMessage')}
				confirmActionText={t('Upload')}
				onConfirm={handleConfirmUpload}
				onCancel={() => setShowConfirmModal(false)}
				onClose={() => setShowConfirmModal(false)}
				type="confirm"
				confirmBtnLoading={uploadLoading}
			/>
		</div>
	);
};

export default SignedDocuments;
